import {
  ReflectionsResponse,
  UserLatestAssessmentResponse,
  assessmentQuestionnaireApiHandler,
  useUserAssessmentHooks,
} from '@api/assessmentQuestionnaireApiHandler';
import { useBoostersAndDrainersHooks } from '@api/boostersAndDrainersApiHandler';
import { fetchCurrentEngagement, fetchUserStreak } from '@api/engagementApiHandler';
import { useGetAllExperts } from '@api/expertsApiHandler';
import { useFocusAreasHooks } from '@api/focusAreasApiHandler';
import { IndicatorsResponse, useIndicatorsHooks } from '@api/indicatorsApiHandler';
import { fetchWellbeingInsights } from '@api/insightsApiHandler';
import {
  fetchIndicatorLearningResources,
  fetchLearningResources,
  useGetAllWpPostsForCarousel,
} from '@api/learningApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import { useUserDetails } from '@api/userApiHandler';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ApiResponse } from '@interfaces/index';
import { Grid, Skeleton } from '@mui/material';
import { MotionText } from '@shared/atoms';
import { LearningArticleCard, LearningCarousel, LoadingBackdrop } from '@shared/molecules';
import {
  ExpertsSection,
  JournalSection,
  JourneySection,
  LearningSection,
  WellbeingIndicators,
  WellbeingPlan,
  WellbeingProfile,
  WellbeingTrends,
} from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateToShortMonth } from '@utils/date.util';
import { DEFAULT_HS_BLUE_COLOR } from 'custom.d';
import React, { useEffect, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import { resetQuestionnaire } from 'store/Slices/QuestionnaireSliceV2';
import {
  JournalSectionSkeleton,
  JourneySectionSkeleton,
  WellbeingIndicatorsSkeleton,
  WellbeingPlanSkeleton,
  WellbeingProfileSkeleton,
  WellbeingTrendsSkeleton,
} from './LoadingSkeletons';
import styles from './styles.module.scss';

export const WellbeingDashboard: React.FC = () => {
  const { isDesktop } = useWindowDimensions();
  const history = useHistory();
  const queryClient = useQueryClient();
  const user = localStorage.getItem('user');
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig.data);
  const dispatch = useAppDispatch();
  const isUserDetailsFetching = useIsFetching({ queryKey: ['userDetails'] }) > 0;
  const isPillarsDataFetching = useIsFetching({ queryKey: ['fetchAllUserLatestAssessments'] }) > 0;
  const isCurrentEngagementFetching = useIsFetching({ queryKey: ['fetchCurrentEngagement'] }) > 0;
  const isWellbeingInsightsFetching = useIsFetching({ queryKey: ['fetchWellbeingInsights'] }) > 0;
  const isUserStrikeFetching = useIsFetching({ queryKey: ['fetchUserStreak'] }) > 0;
  const isUserActionsFetching = useIsFetching({ queryKey: ['user-actions'] }) > 0;
  const isActiveFocusAreasFetching = useIsFetching({ queryKey: ['active-focus-areas'] }) > 0;
  const isLatestIndicatorsFetching = useIsFetching({ queryKey: ['user-latest-indicators'] }) > 0;
  const isReflectionsFetching = useIsFetching({ queryKey: ['fetchAllUserReflections'] }) > 0;
  const isBoostersAndDrainersFetching =
    useIsFetching({ queryKey: ['boosters-and-drainers-by-questionnaire-type'] }) > 0;
  const isCheckInFetching =
    useIsFetching({
      queryKey: ['user-assessments-by-questionnaire-type', 'checkIn'],
    }) > 0;

  useEffect(() => {
    dispatch(resetQuestionnaire());
  }, []);

  const {
    data: userDetails,
    isLoading: isLoadingUser,
    isError: isUserError,
    error: userError,
  } = useUserDetails(user);

  const fetchAllUserLatestAssessments = async (): Promise<
    ApiResponse<UserLatestAssessmentResponse[]>
  > => assessmentQuestionnaireApiHandler.getUserLatestAssessments();

  const {
    data: pillarsData,
    isLoading: pillarsLoading,
    error: pillarsError,
  } = useQuery({
    queryKey: ['fetchAllUserLatestAssessments'],
    queryFn: fetchAllUserLatestAssessments,
    enabled: true,
  });

  const {
    data: currentEngagementData,
    isLoading: currentEngagementsLoading,
    error: currentEngagementsErrors,
  } = useQuery({
    queryKey: ['fetchCurrentEngagement'],
    queryFn: fetchCurrentEngagement,
    enabled: true,
  });

  const {
    data: wellbeingInsightData,
    isLoading: wellbeingInsightDataLoading,
    error: wellbeingInsightDataErrors,
  } = useQuery({
    queryKey: ['fetchWellbeingInsights'],
    queryFn: fetchWellbeingInsights,
    enabled: true,
  });

  const {
    data: engagementUserStreak,
    isLoading: engagementUserStreakLoading,
    error: engagementUserStreakErrors,
  } = useQuery({
    queryKey: ['fetchUserStreak'],
    queryFn: fetchUserStreak,
    enabled: true,
  });

  const {
    data: userActionsCompletedResponse,
    isLoading: isLoadingCompletedActions,
    error: actionsCompletedError,
  } = useUserActionsHooks.useUserActions({
    completed: true,
  });

  const {
    data: userActionsResponse,
    isLoading: isLoadingActions,
    error: actionsError,
    refetch: refetchActions,
  } = useUserActionsHooks.useUserActions({
    completed: false,
  });

  const {
    data: activeFocusAreasResponse,
    isLoading: isLoadingActive,
    error: activeError,
    refetch: refetchActive,
  } = useFocusAreasHooks.useActiveFocusAreas();

  const {
    data: latestIndicators,
    isLoading: isLatestIndicatorsLoading,
    error: latestIndicatorsError,
    refetch: refetchLatestIndicators,
  } = useIndicatorsHooks.useLatestIndicators();

  const {
    data: learningResources,
    isLoading: learningResourcesLoading,
    error: learningResourcesErrors,
  } = useQuery({
    queryKey: ['fetchLearningResources'],
    queryFn: fetchLearningResources,
    enabled: true,
  });

  const fetchAllUserReflections = async (): Promise<ApiResponse<ReflectionsResponse[]>> =>
    assessmentQuestionnaireApiHandler.getReflections();

  const {
    data: reflectionsData,
    isLoading: reflectionsLoading,
    error: reflectionsErrors,
  } = useQuery({
    queryKey: ['fetchAllUserReflections'],
    queryFn: fetchAllUserReflections,
    enabled: true,
  });

  const {
    data: checkInAssessments,
    isLoading: checkInAssessmentsLoading,
    error: checkInAssessmentsError,
    refetch: refetchCheckInAssessments,
  } = useUserAssessmentHooks.useUserAssessmentsByQuestionnaireType(
    QuestionnaireType.CHECK_IN,
    true,
  );

  const {
    data: boostersAndDrainersByType,
    isLoading: loadingBoostersAndDrainersByType,
    isError: errorBoostersAndDrainersByType,
  } = useBoostersAndDrainersHooks.useBoostersAndDrainersByQuestionnaireType(
    QuestionnaireType.CORE_PILLAR,
  );

  const {
    data: learningResourcesForRecommended,
    isLoading: learningResourcesForRecommendedLoading,
    error: learningResourcesForRecommendedErrors,
  } = useQuery({
    queryKey: ['fetchIndicatorLearningResources', 1],
    queryFn: () => fetchIndicatorLearningResources(1),
    enabled: true,
  });

  const { data: posts = [], isLoading: loadingPosts } = useGetAllWpPostsForCarousel();
  const { data: expertsData = { results: [], total: 0 }, isLoading: expertsLoading } =
    useGetAllExperts();

  const learningsForDashboardRecommended:
    | { id: number; innerImageUrl: string; title: string }[]
    | [] = useMemo(() => {
    if (learningResourcesForRecommended?.success) {
      return (
        learningResourcesForRecommended?.responseObject.map((dt) => ({
          id: dt.id,
          innerImageUrl: dt.imageUrl,
          title: dt.title,
        })) || []
      );
    }

    return [];
  }, [learningResourcesForRecommended]);

  const userActions = useMemo(() => {
    if (!userActionsResponse?.success) return [];

    const actions = userActionsResponse.responseObject || [];
    const sorted = [...actions].sort((a, b) => b.orderId - a.orderId);

    return sorted;
  }, [userActionsResponse]);

  const journeyData = useMemo(() => {
    const hasEngagementData =
      currentEngagementData?.success && currentEngagementData?.responseObject;
    const hasStreakData = engagementUserStreak?.success && engagementUserStreak?.responseObject;
    const hasCompletedActions =
      userActionsCompletedResponse?.success && userActionsCompletedResponse?.responseObject;
    const hasInCompletedActions =
      userActionsResponse?.success && userActionsResponse?.responseObject;
    const hasLearningResources = learningResources?.success && learningResources?.responseObject;

    return {
      points: hasEngagementData ? currentEngagementData.responseObject.totalPoints : 0,
      currentStreak: hasStreakData ? engagementUserStreak.responseObject.currentStreak : 0,
      actionsCompleted: hasCompletedActions
        ? userActionsCompletedResponse?.responseObject?.length
        : 0,
      currentTier: hasEngagementData ? currentEngagementData.responseObject.currentTier : 'None',
      nextAction: hasInCompletedActions
        ? userActions.filter((ac) => ac.startedAt === null)[0]
        : null,
      hoursCompleted: hasLearningResources ? learningResources?.responseObject?.totalContent : 0,
    };
  }, [
    currentEngagementData,
    engagementUserStreak,
    userActionsCompletedResponse,
    userActionsResponse,
    userActions,
    learningResources,
  ]);

  const remainingActions = useMemo(() => {
    const hasRemainingActions = userActionsResponse?.success && userActionsResponse?.responseObject;

    return {
      actions: hasRemainingActions ? userActions?.slice(0, 5) : [],
    };
  }, [userActionsResponse, userActions]);

  const wellbeingProfileData = useMemo(() => {
    const hasActiveFocusAreas =
      activeFocusAreasResponse?.success && activeFocusAreasResponse?.responseObject;
    const hasPillars = pillarsData?.success && pillarsData?.responseObject;
    const hasBoostersAndDrainers =
      boostersAndDrainersByType?.success && boostersAndDrainersByType?.responseObject;
    const hasWellbeingInsights =
      wellbeingInsightData?.success && wellbeingInsightData?.responseObject;

    const modifiedInsights = hasWellbeingInsights
      ? {
          insightDetails: {
            id: wellbeingInsightData?.responseObject?.insightDetails?.id || 0,
            userId: wellbeingInsightData?.responseObject?.insightDetails?.userId || 0,
            wellbeingSnapshot:
              wellbeingInsightData?.responseObject?.insightDetails?.wellbeingSnapshot || null,
            wellbeingStyle:
              wellbeingInsightData?.responseObject?.insightDetails?.wellbeingStyle || null,
            patterns: wellbeingInsightData?.responseObject?.insightDetails?.patterns || [],
            trigger: wellbeingInsightData?.responseObject?.insightDetails?.trigger || [],
            createdAt: wellbeingInsightData?.responseObject?.insightDetails?.createdAt || '',
            updatedAt: wellbeingInsightData?.responseObject?.insightDetails?.updatedAt || '',
            recommendedActions:
              wellbeingInsightData?.responseObject?.insightDetails?.recommendedActions || [],
          },
          questionnairesCompleted:
            wellbeingInsightData?.responseObject?.questionnairesCompleted || 0,
        }
      : {
          insightDetails: {
            id: 0,
            userId: 0,
            wellbeingSnapshot: null,
            wellbeingStyle: null,
            patterns: [],
            trigger: [],
            createdAt: '',
            updatedAt: '',
            recommendedActions: [],
          },
          questionnairesCompleted: 0,
        };

    const allRelevantPillars = hasPillars
      ? pillarsData.responseObject.filter(
          (pd) =>
            pd.questionnaireType === QuestionnaireType.CORE_PILLAR ||
            pd.questionnaireType === QuestionnaireType.HOLISTIC,
        )
      : [];

    const completedPillars = allRelevantPillars.filter((pd) => pd.latestAssessment);

    const completionPercentage =
      allRelevantPillars.length > 0
        ? Math.round((completedPillars.length / allRelevantPillars.length) * 100)
        : 0;

    const nextAssessment = allRelevantPillars?.filter((ap) => !ap.latestAssessment)[0] || null;

    const boosters =
      hasBoostersAndDrainers &&
      modifiedInsights?.questionnairesCompleted &&
      modifiedInsights?.questionnairesCompleted >= 3
        ? boostersAndDrainersByType?.responseObject?.topBoosters
            .filter((bs) => bs?.question?.label && bs?.question?.label?.trim().length > 0)
            .map((bs) => bs.question.label)
        : [];

    const drainers =
      hasBoostersAndDrainers &&
      modifiedInsights?.questionnairesCompleted &&
      modifiedInsights?.questionnairesCompleted >= 3
        ? boostersAndDrainersByType?.responseObject?.topDrainers
            .filter((bs) => bs?.question?.label && bs?.question?.label?.trim().length > 0)
            .map((bs) => bs.question.label)
        : [];

    return {
      focusAreas: hasActiveFocusAreas ? activeFocusAreasResponse.responseObject : [],
      pillarsData: allRelevantPillars,
      completionPercentage,
      boosters,
      drainers,
      wellbeingInsights: modifiedInsights,
      nextAssessment,
      lastUpdatedAt: hasWellbeingInsights
        ? wellbeingInsightData?.responseObject?.insightDetails?.createdAt
          ? formatDateToShortMonth(wellbeingInsightData?.responseObject?.insightDetails?.createdAt)
          : null
        : null,
    };
  }, [activeFocusAreasResponse, pillarsData, boostersAndDrainersByType, wellbeingInsightData]);

  const sortedReflections = useMemo(() => {
    if (reflectionsData?.success) {
      return [...reflectionsData.responseObject].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }

    return [];
  }, [reflectionsData]);

  const wellbeingTrendsData = useMemo(() => {
    if (checkInAssessments?.success) {
      return checkInAssessments?.responseObject || [];
    }

    return [];
  }, [checkInAssessments]);

  const wellbeingIndicators: IndicatorsResponse[] | [] = useMemo(() => {
    if (latestIndicators?.success) {
      return latestIndicators?.responseObject || [];
    }

    return [];
  }, [latestIndicators]);

  const isJourneyLoading =
    currentEngagementsLoading ||
    engagementUserStreakLoading ||
    isLoadingCompletedActions ||
    isLoadingActions ||
    learningResourcesLoading;

  const isTrendsLoading = checkInAssessmentsLoading;
  const isJournalLoading = reflectionsLoading;
  const isProfileLoading =
    isLoadingActive ||
    pillarsLoading ||
    loadingBoostersAndDrainersByType ||
    wellbeingInsightDataLoading;
  const isWellbeingIndicatorsLoading = isLatestIndicatorsLoading;
  const isPlanLoading = isLoadingActions;

  const overallLoading = isJourneyLoading;

  const journeyPanelReFetching =
    isCurrentEngagementFetching || isUserStrikeFetching || isUserActionsFetching;
  const profileReFetching =
    isPillarsDataFetching ||
    isWellbeingInsightsFetching ||
    isActiveFocusAreasFetching ||
    isBoostersAndDrainersFetching;
  const trendsReFetching = isCheckInFetching;
  const journalsReFetching = isReflectionsFetching;
  const indicatorsReFetching = isLatestIndicatorsFetching;
  const plansReFetching = isUserActionsFetching;

  const refetchDataAfterCheckIn = () => {
    refetchCheckInAssessments();
    refetchLatestIndicators();
    queryClient.invalidateQueries({ queryKey: ['fetchCurrentEngagement'] });
    queryClient.invalidateQueries({ queryKey: ['fetchUserStreak'] });
  };

  return (
    <CoreLayout title="Wellbeing Dashboard" navigationType="default" fullWidthEnabled>
      {overallLoading && (
        <LoadingBackdrop text="Preparing your personalised wellbeing dashboard…" />
      )}

      <div
        className={styles.dashboardContainer}
        role="main"
        style={{
          backgroundColor: companyConfigData.dashboardBgColour
            ? companyConfigData.dashboardBgColour
            : DEFAULT_HS_BLUE_COLOR,
        }}
      >
        <div className="container">
          <header className={styles.dashboardHeader}>
            {isLoadingUser || isUserDetailsFetching ? (
              <>
                <Skeleton
                  variant="text"
                  width="50%"
                  height={80}
                  className={styles.loadUserWelcome}
                />
              </>
            ) : (
              <MotionText
                className={styles.title}
                as="h2"
                text={
                  userDetails
                    ? companyConfigData.platformName
                      ? `Welcome to ${companyConfigData.platformName}, ${userDetails?.forename}`
                      : `Welcome to Hapstar, ${userDetails?.forename}`
                    : 'Welcome to Hapstar platform'
                }
              />
            )}
          </header>

          <div className={styles.dashboardContent}>
            {isJourneyLoading || journeyPanelReFetching ? (
              <JourneySectionSkeleton />
            ) : (
              <JourneySection
                engagementPoints={journeyData.points}
                hoursCompleted={journeyData.hoursCompleted}
                currentStreak={journeyData.currentStreak}
                actionsCompleted={journeyData.actionsCompleted}
                currentTier={journeyData.currentTier}
                nextAction={journeyData?.nextAction}
                refetchActions={refetchActions}
                nextAssessment={wellbeingProfileData?.nextAssessment}
              />
            )}

            {isProfileLoading || profileReFetching ? (
              <WellbeingProfileSkeleton />
            ) : (
              <WellbeingProfile
                lastUpdated={wellbeingProfileData?.lastUpdatedAt || null}
                completionPercentage={wellbeingProfileData?.completionPercentage}
                boosters={wellbeingProfileData.boosters}
                drainers={wellbeingProfileData.drainers}
                focusAreas={wellbeingProfileData.focusAreas}
                pillars={wellbeingProfileData?.pillarsData}
                wellbeingInsights={wellbeingProfileData?.wellbeingInsights}
                nextAssessment={wellbeingProfileData?.nextAssessment}
              />
            )}

            <div className={styles.trendsAndJournalRow}>
              {isTrendsLoading || trendsReFetching ? (
                <WellbeingTrendsSkeleton />
              ) : (
                <WellbeingTrends
                  wellbeingTrendsData={wellbeingTrendsData?.slice(0, 10) || []}
                  refetchCheckInAssessments={refetchDataAfterCheckIn}
                />
              )}

              {isJournalLoading || journalsReFetching ? (
                <JournalSectionSkeleton />
              ) : (
                <JournalSection reflections={sortedReflections} />
              )}
            </div>

            {isWellbeingIndicatorsLoading || indicatorsReFetching ? (
              <WellbeingIndicatorsSkeleton />
            ) : (
              <WellbeingIndicators indicators={wellbeingIndicators} />
            )}

            {isPlanLoading || plansReFetching ? (
              <WellbeingPlanSkeleton />
            ) : (
              <WellbeingPlan
                actionItems={remainingActions?.actions}
                refetchActions={refetchActions}
                recommendedNextActions={
                  wellbeingProfileData?.wellbeingInsights?.insightDetails?.recommendedActions || []
                }
                recommendedNextActionsLoading={isWellbeingInsightsFetching}
              />
            )}

            {learningsForDashboardRecommended && (
              <LearningSection
                posts={learningsForDashboardRecommended}
                loadingPosts={learningResourcesForRecommendedLoading}
              />
            )}

            {companyConfigData &&
              companyConfigData?.expertsEnabled &&
              expertsData?.results?.length > 0 && (
                <ExpertsSection experts={expertsData} expertsLoading={expertsLoading} />
              )}
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.bottomBlogs}>
          <MotionText
            id="recommended-heading"
            className={styles.postsTitle}
            as="h3"
            text="Latest Learning"
          />

          {isDesktop ? (
            <Grid container spacing={4} className={styles.learningItemsContainer}>
              {loadingPosts ? (
                <>
                  <Grid item xs={12} md={4}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height="275px"
                      sx={{ borderRadius: 8 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height="275px"
                      sx={{ borderRadius: 8 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height="275px"
                      sx={{ borderRadius: 8 }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  {posts?.slice(0, 3).map((post, index) => (
                    <Grid item xs={12} md={4} key={index}>
                      <LearningArticleCard
                        data={post}
                        loading={loadingPosts}
                        sectionType={post.id === -999 ? 'allLearnings' : 'posts'}
                        navigateTo="oldPosts"
                      />
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          ) : (
            <LearningCarousel
              data={posts?.slice(0, 3)}
              loading={loadingPosts}
              carouselType="categoryPosts"
              navigate={(id: number, title: string) => history.push(`/post/${id}`)}
            />
          )}

          {posts?.length > 0 && (
            <div className={styles.postsBtnContainer}>
              <Link to="/posts" className={styles.moreLearningText}>
                <div>More Learning</div>
              </Link>
            </div>
          )}

          {!loadingPosts && posts?.length === 0 && <p>No posts</p>}
        </div>
      </div>
    </CoreLayout>
  );
};
